import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import WorkloadAnalysis from "./WorkloadAnalysis";
import TargetHours from "./TargetHours";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Row = {
  id: string;
  crew: string | null;
  status: string;
  scheduled_date: string;
  est_duration_minutes: number | null;
  actual_duration_minutes: number | null;
  customer_id: string | null;
  customer_name: string;
  customers: { price: number | null } | null;
};

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const fmtHrs = (mins: number) => `${Math.round((mins / 60) * 10) / 10}h`;

const daysAgoISO = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default function Reports({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [tab, setTab] = useState<"performance" | "workload" | "targets">("performance");


  useEffect(() => {
    let active = true;
    setLoading(true);
    supabase
      .from("jobs")
      .select(
        "id, crew, status, scheduled_date, est_duration_minutes, actual_duration_minutes, customer_id, customer_name, customers!left(price)"
      )
      .eq("company_id", companyId)
      .gte("scheduled_date", daysAgoISO(range))
      .lte("scheduled_date", new Date().toISOString().slice(0, 10))
      .limit(5000)
      .then(({ data, error }) => {
        if (!active) return;
        if (error) toast.error("Could not load reporting data");
        setRows((data as unknown as Row[]) ?? []);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId, range]);

  const completed = useMemo(() => rows.filter((r) => r.status.startsWith("complete")), [rows]);

  const byCrew = useMemo(() => {
    const map = new Map<
      string,
      { crew: string; revenue: number; jobs: number; minutes: number; estMinutes: number }
    >();
    completed.forEach((r) => {
      const key = r.crew ?? "Unassigned";
      const prev =
        map.get(key) ?? { crew: key, revenue: 0, jobs: 0, minutes: 0, estMinutes: 0 };
      const est = r.est_duration_minutes ?? 45;
      map.set(key, {
        crew: key,
        revenue: prev.revenue + Number(r.customers?.price ?? 0),
        jobs: prev.jobs + 1,
        minutes: prev.minutes + (r.actual_duration_minutes ?? est),
        estMinutes: prev.estMinutes + est,
      });
    });
    return Array.from(map.values())
      .map((c) => ({
        ...c,
        perHour: c.minutes > 0 ? (c.revenue / c.minutes) * 60 : 0,
        drift: c.estMinutes > 0 ? Math.round(((c.minutes - c.estMinutes) / c.estMinutes) * 100) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [completed]);

  const totals = useMemo(() => {
    const revenue = byCrew.reduce((s, c) => s + c.revenue, 0);
    const minutes = byCrew.reduce((s, c) => s + c.minutes, 0);
    const timed = completed.filter((r) => r.actual_duration_minutes != null);
    const avgActual = timed.length
      ? Math.round(timed.reduce((s, r) => s + (r.actual_duration_minutes ?? 0), 0) / timed.length)
      : null;
    const avgEst = completed.length
      ? Math.round(
          completed.reduce((s, r) => s + (r.est_duration_minutes ?? 45), 0) / completed.length
        )
      : null;
    const canceled = rows.filter((r) => r.status === "canceled").length;
    return {
      revenue,
      minutes,
      jobs: completed.length,
      perHour: minutes > 0 ? (revenue / minutes) * 60 : 0,
      avgActual,
      avgEst,
      canceled,
      timedShare: completed.length ? Math.round((timed.length / completed.length) * 100) : 0,
    };
  }, [byCrew, completed, rows]);

  const topCustomers = useMemo(() => {
    const map = new Map<string, { name: string; revenue: number; jobs: number }>();
    completed.forEach((r) => {
      const key = r.customer_id ?? r.customer_name;
      const prev = map.get(key) ?? { name: r.customer_name, revenue: 0, jobs: 0 };
      map.set(key, {
        name: r.customer_name,
        revenue: prev.revenue + Number(r.customers?.price ?? 0),
        jobs: prev.jobs + 1,
      });
    });
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [completed]);

  const exportCsv = () => {
    const header = "crew,jobs,revenue,hours,revenue_per_hour,est_vs_actual_pct\n";
    const body = byCrew
      .map((c) =>
        [
          c.crew,
          c.jobs,
          c.revenue.toFixed(2),
          (c.minutes / 60).toFixed(2),
          c.perHour.toFixed(2),
          c.drift,
        ].join(",")
      )
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mowzi-crew-report-${range}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold">Reports</h2>
        <div className="flex items-center gap-2">
          {tab === "performance" && (
            <>
              {[30, 90, 365].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={range === d ? "default" : "outline"}
                  onClick={() => setRange(d)}
                >
                  {d === 365 ? "1 year" : `${d} days`}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={exportCsv} disabled={!byCrew.length}>
                Export CSV
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="inline-flex rounded-lg border bg-muted/40 p-1">
        {(
          [
            { id: "performance", label: "Performance" },
            { id: "workload", label: "Workload" },
            { id: "targets", label: "Work day targets" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "workload" && <WorkloadAnalysis companyId={companyId} embedded />}

      {tab === "targets" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Work day targets</CardTitle>
          </CardHeader>
          <CardContent>
            <TargetHours companyId={companyId} isOwner embedded />
          </CardContent>
        </Card>
      )}

      {tab === "performance" &&
        (loading ? (
          <p className="text-sm text-muted-foreground">Crunching numbers…</p>
        ) : (
          <>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Revenue</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-bold text-primary">
                {money(totals.revenue)}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Jobs completed</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-bold">
                {totals.jobs}
                {totals.canceled > 0 && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {totals.canceled} canceled
                  </span>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Revenue per hour</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-bold">{money(totals.perHour)}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Estimate accuracy</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {totals.avgActual == null ? (
                  <span className="text-muted-foreground">No timed jobs yet</span>
                ) : (
                  <>
                    <div className="text-2xl font-bold">
                      {totals.avgActual} min <span className="text-sm font-normal">actual avg</span>
                    </div>
                    <div className="text-muted-foreground">
                      vs {totals.avgEst} min estimated · {totals.timedShare}% of stops timed
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Revenue per crew</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              {byCrew.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No completed jobs in this window yet.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byCrew}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="crew" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v: number, name) =>
                        name === "revenue" ? money(v) : `${Math.round(v)}`
                      }
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {byCrew.map((c) => (
                        <Cell key={c.crew} fill="hsl(var(--primary))" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Crew productivity</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground">
                    <tr className="[&>th]:px-2 [&>th]:py-1">
                      <th>Crew</th>
                      <th className="text-right">Jobs</th>
                      <th className="text-right">Hours</th>
                      <th className="text-right">Revenue</th>
                      <th className="text-right">$/hr</th>
                      <th className="text-right">Est vs actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCrew.map((c) => (
                      <tr
                        key={c.crew}
                        className="border-t border-border even:bg-muted/40 [&>td]:px-2 [&>td]:py-1"
                      >
                        <td className="font-medium">{c.crew}</td>
                        <td className="text-right">{c.jobs}</td>
                        <td className="text-right">{fmtHrs(c.minutes)}</td>
                        <td className="text-right">{money(c.revenue)}</td>
                        <td className="text-right">{money(c.perHour)}</td>
                        <td
                          className={
                            c.drift > 10
                              ? "text-right font-medium text-destructive"
                              : "text-right text-muted-foreground"
                          }
                        >
                          {c.drift > 0 ? `+${c.drift}%` : `${c.drift}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top customers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-xs">
                {topCustomers.length === 0 ? (
                  <p className="text-muted-foreground">No revenue recorded yet.</p>
                ) : (
                  topCustomers.map((c) => (
                    <div key={c.name} className="flex items-center justify-between gap-2">
                      <span className="truncate">{c.name}</span>
                      <span className="text-muted-foreground">{c.jobs} mows</span>
                      <span className="font-semibold">{money(c.revenue)}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
          </>
        ))}

    </div>
  );
}
